import React, { useState, useEffect, useRef } from 'react';
import MoneyUtils from '../utils/moneyUtils';
import { formatPromoLabel, calcPromoDiscount } from '../utils/promoUtils';


// ─── Single Promo Card ────────────────────────────────────────────────────────

// Helper to check if a color string looks like a hex
const isValidHex = (color) => /^#([0-9A-F]{3}){1,2}$/i.test(color);

const hexToRgb = (hex) => {
  if (!isValidHex(hex)) return { r: 100, g: 80, b: 220 };
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) }
    : { r: 100, g: 80, b: 220 };
};

const shiftHue = (hex, amount) => {
  const { r, g, b } = hexToRgb(hex);
  const clamp = (v) => Math.max(0, Math.min(255, v));
  // A slightly better pseudo-hue shift for vibrancy
  return `rgb(${clamp(r + amount)}, ${clamp(g - amount * 0.4)}, ${clamp(b + amount * 0.8)})`;
};

const adjustColors = (hex, brightness, saturation = 1) => {
  const { r, g, b } = hexToRgb(hex);
  const clamp = (v) => Math.max(0, Math.min(255, Math.floor(v)));
  return `rgb(${clamp(r * brightness * saturation)}, ${clamp(g * brightness * saturation)}, ${clamp(b * brightness * saturation)})`;
};

// Simple hash from string to a number (used to generate consistent colors based on promo ID)
const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
};

// A vibrant set of base hex colors to pick from for dynamic offers
const VIBRANT_PALETTES = [
  '#FF1D58', '#FF4E00', '#D90368', '#7F00FF', '#0070FF', '#05D5FF', 
  '#00B894', '#6C5CE7', '#FD79A8', '#E84393', '#E17055', '#D63031',
  '#0984E3', '#006266'
];

// ─── Wave SVG Layer ───────────────────────────────────────────────────────────

const WaveLayers = ({ primaryHex, id }) => {
  const { r, g, b } = hexToRgb(primaryHex);
  // Sanitize ID even further to be a pure alphanumeric string for SVG
  const safeId = `vib-${id?.toString().replace(/[^a-zA-Z0-9]/g, '') || Math.random().toString(36).substring(7)}`;
  
  const lighten = (a) => `rgba(${Math.min(r + 85, 255)},${Math.min(g + 65, 255)},${Math.min(b + 35, 255)},${a})`;
  const darken  = (a) => `rgba(${Math.max(r - 60, 0)},${Math.max(g - 50, 0)},${Math.max(b + 15, 0)},${a})`;
  const white   = (a) => `rgba(255,255,255,${a})`;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none opacity-100"
      preserveAspectRatio="none"
      viewBox="0 0 360 115"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={`wg1-${safeId}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={lighten(0.85)} />
          <stop offset="100%" stopColor={lighten(0.15)} />
        </linearGradient>
        <linearGradient id={`wg2-${safeId}`} x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={darken(0.7)} />
          <stop offset="100%" stopColor={darken(0.2)} />
        </linearGradient>
        <radialGradient id={`rg1-${safeId}`} cx="70%" cy="30%" r="65%">
          <stop offset="0%" stopColor={lighten(0.6)} />
          <stop offset="100%" stopColor={lighten(0)} />
        </radialGradient>
      </defs>

      <path d="M 0 115 L 360 115 L 360 15 C 320 -10, 240 40, 180 0 C 120 -40, 40 50, 0 15 Z" fill={`url(#wg2-${safeId})`} opacity="0.4" />
      <path d="M 210 115 C 180 55, 290 0, 360 15 L 360 115 Z" fill={`url(#wg2-${safeId})`} />
      <path d="M 100 115 C 145 35, 285 15, 360 85 L 360 115 Z" fill={`url(#wg1-${safeId})`} />
      <path d="M 0 115 L 0 40 C 70 15, 160 75, 210 115 Z" fill={white(0.18)} />

      <ellipse cx="320" cy="15" rx="100" ry="60" fill={`url(#rg1-${safeId})`} />
      <path d="M 0 85 Q 75 75, 150 90 Q 225 105, 300 80 Q 330 70, 360 85 L 360 115 L 0 115 Z" fill={white(0.12)} />

      <circle cx="210" cy="20" r="28" fill={white(0.08)} />
      <circle cx="345" cy="105" r="40" fill={darken(0.35)} />
    </svg>
  );
};

// ─── Floating Decorations ─────────────────────────────────────────────────────

const FloatingDots = () => (
  <>
    {/* Bold letter watermarks */}
    <span className="absolute select-none pointer-events-none font-black text-white/10 text-[60px] leading-none top-[-8px] right-[6px]">a</span>
    <span className="absolute select-none pointer-events-none font-black text-white/10 text-[50px] leading-none bottom-[-6px] left-[38%]">a</span>
    <span className="absolute select-none pointer-events-none font-black text-white/10 text-[38px] leading-none top-[4px] right-[28%]">a</span>

    {/* Plus cross marks */}
    <span className="absolute top-[12%] left-[48%] text-white/30 text-[14px] font-light leading-none select-none">+</span>
    <span className="absolute top-[35%] right-[12%] text-white/30 text-[12px] font-light leading-none select-none">+</span>
    <span className="absolute bottom-[22%] right-[38%] text-white/30 text-[12px] font-light leading-none select-none">+</span>

    {/* Small circle outline */}
    <div className="absolute top-[28%] right-[32%] w-2 h-2 rounded-full border border-white/40" />
  </>
);

const PromoCard = ({ promo, selected, onSelect, theme }) => {
  const isSelected = selected?.id === promo.id;
  
  const hash = hashString(promo.id || promo.title || 'default');
  
  // Deterministic color assignment from our vibrancy palette
  const paletteColor = VIBRANT_PALETTES[hash % VIBRANT_PALETTES.length];
  
  // Use vibrant palettes for everything except purely on-brand cases to ensure no grey cards
  const primaryHex = (hash % 10 === 0 && theme?.primary && theme.primary !== 'transparent') 
    ? theme.primary 
    : paletteColor;

  const { r, g, b } = hexToRgb(primaryHex);
  const gradFrom = primaryHex;
  const gradMid  = shiftHue(primaryHex, 38);
  const gradTo   = shiftHue(primaryHex, -22);

  // Force WAVE texture by default unless there's a strong image URL
  const hasImage = !!(promo.imageUrl && promo.imageUrl.length > 10 && !promo.imageUrl.includes('null'));
  const background = hasImage 
    ? `linear-gradient(rgba(0,0,0,0.8), rgba(0,0,0,0.25)), url(${promo.imageUrl}) center/cover`
    : `linear-gradient(135deg, ${gradFrom} 0%, ${gradMid} 50%, ${gradTo} 100%)`;

  const validTo = new Date(promo.validTo);
  const dateStr = validTo.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });

  let discountMain = '';
  let discountSub = '';
  if (promo.discountType === 'flat') {
    const formatted = MoneyUtils.format(promo.discountValue);
    // Split to remove .00 or make it smaller
    if (formatted.endsWith('.00')) {
      discountMain = formatted.slice(0, -3);
      discountSub = 'Flat Off';
    } else {
      discountMain = formatted;
      discountSub = 'Flat Off';
    }
  } else {
    discountMain = `${promo.discountValue || promo.discountPercentage || 0}%`;
    discountSub = 'OFF*';
  }

  return (
    <div
      onClick={() => onSelect && onSelect(isSelected ? null : promo)}
      className="relative flex-shrink-0 w-full overflow-hidden cursor-pointer shadow-sm select-none transition-transform"
      style={{ 
        height: '101px', // Reduced another 8% (~110px -> 101px)
        backgroundColor: primaryHex, 
        background: background,
        borderRadius: '16px',
      }}
    >
      {/* Wave layers */}
      {!hasImage && <WaveLayers primaryHex={primaryHex} id={promo.id || hash} />}

      {/* Floating letter / dot decorations */}
      {!hasImage && <FloatingDots />}

      {/* Selected overlay border */}
      {isSelected && (
        <div 
          className="absolute inset-0 pointer-events-none z-10" 
          style={{ 
            border: `3px solid ${secondaryHex}`, 
            borderRadius: '16px',
            boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.25)`,
          }} 
        />
      )}

      {/* Internal Content */}
      <div className="relative z-10 p-[10px] h-full flex flex-col justify-between box-border">
        <div className="flex justify-between items-start">
          <div className="flex flex-col text-white">
            <span className="text-[7.5px] font-bold tracking-[0.14em] text-white/90 uppercase mb-[1px] font-sans">Discount up to</span>
            <div className="flex items-baseline drop-shadow-md">
              <span className="text-[34px] font-black leading-none font-sans tracking-[-1.5px] text-white">{discountMain}</span>
              <span className="text-[9.5px] font-extrabold ml-1 text-white/95 tracking-[0.02em]">{discountSub}</span>
            </div>
          </div>
          
          <div className="px-2 py-[2px] border border-white/40 rounded-md bg-white/10 backdrop-blur-sm mt-[1px] flex items-center justify-center min-w-[40px]">
             <span className="text-[8.5px] font-bold text-white tracking-[0.1em] uppercase whitespace-nowrap text-center">
               {promo.title || 'PROMO'}
             </span>
          </div>
        </div>

        <div className="flex justify-between items-end pb-0.5">
          <div className="text-[10px] text-white/80 font-medium max-w-[150px] leading-[1.3] font-sans">
            {promo.minOrderAmount > 0 
               ? `On orders above ${MoneyUtils.format(promo.minOrderAmount)}` 
               : `Valid till ${dateStr}`}
          </div>
          
          <button 
            className="bg-white px-4 py-[8px] rounded-full text-[10px] font-black tracking-[0.05em] shadow-lg transition-transform active:scale-95 flex items-center gap-1 hover:bg-gray-50 uppercase whitespace-nowrap font-sans"
            style={{ color: isSelected ? secondaryHex : primaryHex, boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}
          >
             {isSelected ? (
                <>
                  <span className="text-current text-[11px] leading-none">✓</span> APPLIED
                </>
             ) : (
                'SHOP NOW'
             )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Promotion Banner Carousel (for Menu Page) ────────────────────────────────

export const PromotionBannerCarousel = ({ promotions = [], theme }) => {
  const scrollRef = useRef(null);
  const timerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  // Handle scroll to update active dot
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const scrollPosition = scrollRef.current.scrollLeft;
    const width = scrollRef.current.offsetWidth;
    // Calculate index based on how far we've scrolled
    const index = Math.round(scrollPosition / width);
    setActiveIndex(index);
  };

  useEffect(() => {
    if (promotions.length <= 1) return;
    
    timerRef.current = setInterval(() => {
      if (!scrollRef.current) return;
      const el = scrollRef.current;
      const cardWidth = el.offsetWidth;
      const maxScroll = el.scrollWidth - el.clientWidth;
      
      // If we are at the end, scroll back to 0, otherwise go to next
      if (el.scrollLeft >= maxScroll - 10) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: cardWidth, behavior: 'smooth' });
      }
    }, 4000); // slightly longer pause for full screen scrolls
    
    return () => clearInterval(timerRef.current);
  }, [promotions.length]);

  if (!promotions || promotions.length === 0) return null;

  return (
    <div className="mx-0 mt-3 mb-1 w-full bg-transparent overflow-hidden">
      {/* Scrollable Container */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory [&::-webkit-scrollbar]:hidden w-full pb-1 px-4 gap-4"
        style={{ scrollBehavior: 'smooth' }}
      >
        {promotions.map((promo) => (
          <div key={promo.id} className="snap-center w-full flex-shrink-0 flex items-center justify-center">
            <PromoCard
              promo={promo}
              theme={theme}
              onSelect={null} // view-only on menu page
              selected={null}
            />
          </div>
        ))}
      </div>
      
      {/* Pagination Dots */}
      {promotions.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
           {promotions.map((_, idx) => (
             <div 
               key={idx} 
               className={`h-1.5 rounded-full transition-all duration-300 ${
                 idx === activeIndex 
                   ? 'w-4 bg-gray-800' 
                   : 'w-1.5 bg-gray-300'
               }`} 
             />
           ))}
        </div>
      )}
    </div>
  );
};

// ─── Offers Bottom Sheet (for Checkout Page) ──────────────────────────────────

export const OffersSheet = ({ isOpen, onClose, promotions = [], appliedPromo, onApply, itemTotal, theme }) => {
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');
  const themeColor = theme?.secondary || '#111';

  // Handle promo code manual entry — try to match against the fetched promotions list
  const handleApplyCode = () => {
    const code = promoCodeInput.trim().toLowerCase();
    if (!code) { setCodeError('Please enter a code'); return; }
    const match = promotions.find(
      (p) => p.id?.toLowerCase() === code || p.title?.toLowerCase() === code
    );
    if (match) {
      onApply(match);
      setCodeError('');
      setPromoCodeInput('');
    } else {
      setCodeError('Invalid or expired promo code');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl max-h-[80vh] flex flex-col shadow-2xl">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        <div className="flex items-center justify-between px-5 pb-3 border-b border-gray-100">
          <h2 className="text-lg font-bold">Offers & Promotions</h2>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500">✕</button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {/* Promo code input */}
          <div className="bg-gray-50 rounded-2xl p-3 border border-dashed border-gray-300">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Enter Promo Code</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCodeInput}
                onChange={(e) => { setPromoCodeInput(e.target.value.toUpperCase()); setCodeError(''); }}
                placeholder="PROMO CODE"
                className="flex-1 px-3 py-2 rounded-xl bg-white border border-gray-200 text-sm font-mono uppercase focus:outline-none focus:ring-1 focus:border-gray-400"
                style={{ letterSpacing: '0.1em' }}
              />
              <button
                onClick={handleApplyCode}
                className="px-4 py-2 rounded-xl text-sm font-bold text-white"
                style={{ backgroundColor: themeColor }}
              >
                Apply
              </button>
            </div>
            {codeError && <p className="text-xs text-red-500 mt-1.5">{codeError}</p>}
          </div>

          {/* Available promotions */}
          {promotions.length > 0 ? (
            <div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Available Offers</p>
              <div className="space-y-3">
                {promotions.map((promo) => {
                  const discountPreview = calcPromoDiscount(itemTotal, promo);
                  const eligible = !promo.minOrderAmount || itemTotal >= promo.minOrderAmount;
                  return (
                    <div
                      key={promo.id}
                      onClick={() => eligible && onApply(appliedPromo?.id === promo.id ? null : promo)}
                      className={`rounded-2xl border-2 p-4 cursor-pointer transition-all ${
                        appliedPromo?.id === promo.id ? 'shadow-md' : 'border-gray-100'
                      } ${!eligible ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={appliedPromo?.id === promo.id ? { borderColor: themeColor, backgroundColor: `${themeColor}10` } : {}}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-black px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: themeColor }}>
                              {formatPromoLabel(promo)}
                            </span>
                            {!eligible && (
                              <span className="text-[10px] text-orange-500 font-semibold">
                                Min {MoneyUtils.format(promo.minOrderAmount)} needed
                              </span>
                            )}
                          </div>
                          <p className="font-bold text-gray-900 text-sm">{promo.title}</p>
                          {promo.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{promo.description}</p>}
                          {promo.terms && <p className="text-[10px] text-gray-400 mt-1">{promo.terms}</p>}
                          {eligible && discountPreview > 0 && (
                            <p className="text-xs font-bold mt-1.5" style={{ color: themeColor }}>
                              You save {MoneyUtils.format(discountPreview)}
                            </p>
                          )}
                        </div>
                        <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center mt-1 transition-all ${
                          appliedPromo?.id === promo.id ? 'text-white text-xs' : 'border-gray-300'
                        }`}
                          style={appliedPromo?.id === promo.id ? { backgroundColor: themeColor, borderColor: themeColor } : {}}
                        >
                          {appliedPromo?.id === promo.id && '✓'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p className="text-3xl mb-2">🎫</p>
              <p className="text-sm">No offers available right now</p>
            </div>
          )}
        </div>

        {/* Done button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-2xl font-bold text-white text-base"
            style={{ backgroundColor: themeColor }}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
};

export default OffersSheet;
